import React, { useState } from "react";
import styled, { css } from "styled-components";
import ReactTooltip from "react-tooltip";
import { toast } from "react-toastify";

import { Icons } from "src/library/components/Icon/types";
import { Icon } from "src/library/components/Icon";
import { Button } from "src/library/components/Button";

import { useDashboardContext } from "../../contexts/DashboardContext";
import { useDashboardApi } from "../../api/dashboard.api";
import separateLayoutOptions from "../../utils/separateLayoutOptions";
import { CreateWidgetModal } from "./CreateWidgetModal";
import { UnsavedDashboardChangesModel } from "./UnsavedDashboardChangesModel";
import { transformGlobalFilters } from "../../utils";

export interface CreateWidgetToolbarProps {
  onClose: () => void;
}

/**
 * A sticky menu view that appears at the bottom of page
 *
 * @returns a horizontal menu react element
 */
const CreateWidgetToolbar: React.ReactFCWithChildren<CreateWidgetToolbarProps> = ({ onClose }) => {
  const {
    currentDashboard,
    layoutsAndWidgets,
    userEditedLayout,
    updateCurrentDashboard,
    updateUserEditedLayout,
    globalFilters
  } = useDashboardContext();

  const [ showCreateWidgetModal, setShowCreateWidgetModal ] = useState(false);
  const [ showUnsavedChangesModal, setShowUnsavedChangesModal ] = React.useState(false);
  const [ updateDashboardResponse, updateDashboardRequest ] = useDashboardApi("UPDATE_DASHBOARD");
  const [ updateDashboardFiltersRes, updateDashboardFiltersReq ] = useDashboardApi("UPDATE_GLOBAL_DASHBOARD_FILTERS");

  const handleSaveDashboard = React.useCallback((): void => {
    // Ensure all data required ready before continuing
    if (!currentDashboard || !layoutsAndWidgets.layouts) {
      toast.error("No dashboard data or layout data to save");

      return;
    }

    // Extract out layout data from grid layouts
    const { layout } = separateLayoutOptions(layoutsAndWidgets.layouts);

    updateDashboardRequest({
      ...currentDashboard,
      widgets: layoutsAndWidgets.widgets,
      layout
    });

    updateDashboardFiltersReq({
      dashboardId: currentDashboard.id,
      params: transformGlobalFilters(globalFilters, currentDashboard.id)
    });
  }, [
    currentDashboard,
    globalFilters,
    layoutsAndWidgets.layouts,
    layoutsAndWidgets.widgets,
    updateDashboardFiltersReq,
    updateDashboardRequest
  ]);

  // We're going to reset the layouts with only what is persisted
  const handleExitEditMode = React.useCallback(() => {
    if (!userEditedLayout) {
      onClose();
    } else {
      setShowUnsavedChangesModal(true);
    }
  }, [ onClose, userEditedLayout ]);

  // handle update dashboard response
  React.useEffect(() => {
    if (updateDashboardResponse.error) {
      toast.error(updateDashboardResponse.error.message);
    }

    if (updateDashboardResponse.data) {
      // update the dashboard in context, which in turn will update the layouts
      updateCurrentDashboard(updateDashboardResponse.data);
      onClose();
    }
  }, [
    onClose,
    updateCurrentDashboard,
    updateDashboardResponse.data,
    updateDashboardResponse.error
  ]);

  // handle update dashboard filters response
  // Handle response when attempting to persist global dashboard filters
  React.useEffect(() => {
    if (updateDashboardFiltersRes.error) {
      toast.error(updateDashboardFiltersRes.error.message);
    }

    if (updateDashboardFiltersRes.data) {
      toast.success("Successfully updated dashboard filters");
    }
  }, [ updateDashboardFiltersRes.data, updateDashboardFiltersRes.error ]);

  return (
    <>
      <ToolbarContainer>
        <div className="widget-actions">
          <Button
            onClick={handleSaveDashboard}
            loading={updateDashboardResponse.pending}
          >
            Save Dashboard
          </Button>

          <Button
            className="add-widget"
            onClick={() => setShowCreateWidgetModal(true)}
          >
            Add Widget
          </Button>
        </div>

        <Icon
          icon={Icons.EXITEDIT}
          $fillColor="primaryLight"
          onClick={handleExitEditMode}
          data-tip
          data-for="exit-edit-tooltip"
          cursor="pointer"
        />

        <ReactTooltip id="exit-edit-tooltip">
          Exit Edit Mode
        </ReactTooltip>

      </ToolbarContainer>

      <CreateWidgetModal
        onDismiss={() => setShowCreateWidgetModal(false)}
        open={showCreateWidgetModal}
      />

      <UnsavedDashboardChangesModel
        open={showUnsavedChangesModal}
        onDismiss={() => setShowUnsavedChangesModal(false)}
        onConfirm={() => {
          // reset changes state
          updateUserEditedLayout(false);
          setShowUnsavedChangesModal(false);
          onClose();
          // refresh dashboard
          currentDashboard && updateCurrentDashboard(currentDashboard);
        }}
      />
    </>
  );
};

const ToolbarContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;

    .widget-actions {
      display: flex;

      .add-widget {
        margin-left: ${theme.spacing.margins.md};
      }
    }
  `}
`;

export default CreateWidgetToolbar;
